import {Regulation, UserRole} from 'types';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import React, {useMemo} from 'react';
import {RegulationsPageRegulationsForm} from './components';
import {useTranslation} from 'react-i18next';
import {REGULATIONS_TRANSLATION_NAMESPACE} from './regulationsConstants';
import {
	InfinityList,
	InfinityListItemWithId,
	InfinityListView,
} from 'components/InfinityList';
import {
	useGetRegulationsNextLazyQuery,
	useGetRegulationsQueryLazyQuery,
	useGetRegulationsSortOrderLazyQuery,
} from './hooks/regulationsInfinity.generated';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {SelectionMode} from 'components/FluentUICompat';
import {EntityContextProvider} from 'components/EntityPage/EntityContext';
import {PAGE_PATHS} from 'pages';
import {useUserContext} from 'authentication/UserContext';
import {
	addFavoriteView,
	useInfinityListStandardHooks,
} from 'components/InfinityList/InfinitListStandardHooks';
import {
	useRegulationsPageColumn,
	useRegulationsPageColumnMetaData,
	useRegulationsPageFilterOptions,
} from './RegulationsPageColumns';
import {handleClickRegulations} from 'components/InfinityList/Excel/XLRegulationsPage';
import {useGetAllKeywordsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {useGetAllMarketsQuery} from 'features/AdminSection/hooks/markets.generated';
import {useVkoKeywordsLazyQuery} from 'features/Dashboard/hooks/vkoKeywords.generated';

const initialSortField = 'regulationNumber';
const initialSortOrder = 'ASC';
const intBatchSize = 50;

const RegulationsPage = () => {
	const {t: tTitle} = useTranslation(REGULATIONS_TRANSLATION_NAMESPACE, {
		keyPrefix: 'RegulationDetail',
	});
	const {t: tViews} = useTranslation('features/regulations', {
		keyPrefix: 'Views',
	});

	const {t: tFav} = useTranslation('appShell/sidenavigation', {
		keyPrefix: 'SideNavigation',
	});

	const {t: tExcel} = useTranslation();

	const {data: keywordData} = useGetAllKeywordsQuery();
	const {data: marketData} = useGetAllMarketsQuery();
	const [getVkoKeywords] = useVkoKeywordsLazyQuery();

	const [getSortOrder] = useGetRegulationsSortOrderLazyQuery();

	const [getRegulationsQuery, {loading: loadingQuery, data: dataQuery}] =
		useGetRegulationsQueryLazyQuery();

	const [getRegulationsNext, {loading: loadingDocuments}] =
		useGetRegulationsNextLazyQuery();

	const [searchParams] = useSearchParams();

	const navigate = useNavigate();

	const {favoriteIds} = useUserContext();

	const views: InfinityListView[] = useMemo(() => {
		const viewsTemp = [
			{headerText: tViews('STD'), accessKey: 'STD', query: '{}'},
			{
				headerText: tViews('EU_REG'),
				accessKey: 'EU_REG',
				query: `{'marketRefs._id' : {$in : ['64808c369c296a2bbb40dd03', '64808c469c296a2bbb40dd14']}}`,
			},
			{
				headerText: tViews('USA_REG'),
				accessKey: 'USA_REG',
				query: `{'marketRefs._id' : '64808c6a9c296a2bbb40dd39'}`,
			},
			{
				headerText: tViews('CHN_REG'),
				accessKey: 'CHN_REG',
				query: `{'marketRefs._id' : '64808c609c296a2bbb40dd25'}`,
			},
			{
				headerText: tViews('MY_DOC'),
				accessKey: 'MY_DOC',
				query: `{'mainKeywordRefs._id' : {$in: [$$_MY_KEYWORDS_$$]}}`,
			},
		];

		addFavoriteView(viewsTemp, favoriteIds, 'Regulation', tFav);
		return viewsTemp;
	}, [favoriteIds]);

	const initialView =
		searchParams.getAll('view').length > 0
			? searchParams.getAll('view').at(0) ?? ''
			: 'STD';

	const arrIds = useMemo(
		() => dataQuery?.regulationsQuery.regulationIds ?? [],
		[dataQuery],
	);

	const columns = useRegulationsPageColumn();
	const columnMetaData = useRegulationsPageColumnMetaData();
	const columnFilterOptions = useRegulationsPageFilterOptions(
		dataQuery,
		keywordData,
		marketData,
	);

	const queryDocumentsDB = async (
		query: string,
		sortField: string,
		sortOrder: string,
	) => {
		if (query.indexOf('$$_MY_KEYWORDS_$$') > -1) {
			const myKeywords = await getVkoKeywords();
			const myKeywordsString =
				myKeywords.data?.myVKOKeywords
					?.map(k => `'${atob(k.id).split('\nd')[1]}'`)
					.join(',') || '';
			query = query.replace('$$_MY_KEYWORDS_$$', myKeywordsString);
		}
		const {data: dataQuery} = await getRegulationsQuery({
			variables: {
				queryArgs: {query, sortField, sortOrder},
			},
		});
		const arrDocumentNext = (dataQuery?.regulationsQuery.regulationsBatch ??
			[]) as Regulation[];
		const arrIDsNew = dataQuery?.regulationsQuery.regulationIds ?? [];
		return {arrDocumentNext, arrIDsNew};
	};

	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const {data: dataDocumentsNext} = await getRegulationsNext({
			variables: {nextArgs: {preFilteredIds}},
		});

		return (dataDocumentsNext?.regulationsNext ?? []) as Regulation[];
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const {data: dataSortOrder} = await getSortOrder({
			variables: {
				sortArgs: {sortField, preFilteredIds},
			},
		});

		return dataSortOrder?.regulationsSortOrder ?? preFilteredIds;
	};

	const handleExcelExport = async (arrDocuments: Regulation[]) => {
		await handleClickRegulations(
			arrDocuments,
			columns.filter(x => x.hiddenAlways === undefined).map(x => x.name),
			tExcel,
		);
	};

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
		excelExportSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds,
		columns,
		columnMetaData,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
		handleExcelExport,
	});

	const handleViewingItem = async (item: Regulation) => {
		await navigate({
			pathname: `${item.id}`,
		});
	};

	const createHref = (item: unknown) =>
		`${PAGE_PATHS.regulations}/${(item as InfinityListItemWithId).id}`;

	const numberOfElements = arrIds.length ?? 1;

	return (
		<ViewAuthorizer
			roles={[
				UserRole.SystemAdministrator,
				UserRole.Vko,
				UserRole.Vex,
				UserRole.VexSupport,
				UserRole.VkoExternal,
				UserRole.ShApprover,
				UserRole.Readers,
				UserRole.RegulationReader,
				UserRole.Bav,
				UserRole.Bedarfskoordinator,
				UserRole.Avk,
			]}
		>
			<EntityContextProvider>
				<RegulationsPageRegulationsForm />
				<InfinityList
					pageName={tTitle('Regulations')}
					items={arrDocument}
					sticky={true}
					columns={columns}
					columnMetaData={columnMetaData}
					columnFilterOptions={columnFilterOptions}
					selectionMode={SelectionMode.single}
					stillLoading={loadingQuery || loadingDocuments}
					initalFilters={linksFilters}
					initialSortField={initialSortField}
					initialSortOrder={initialSortOrder}
					initialView={initialView}
					keyPrefix={keyPrefix}
					firstRender={firstRenderSTD}
					loadItemsNext={loadItemsNextSTD}
					applyFilter={applyFilterSTD}
					applySort={applySortSTD}
					handleInvokeItem={handleViewingItem}
					excelExport={excelExportSTD}
					createHref={createHref}
					numberOfElements={numberOfElements}
					views={views}
				/>
			</EntityContextProvider>
		</ViewAuthorizer>
	);
};

export default RegulationsPage;
